import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
    Alert,
    Box,
    Tooltip,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Grid,
    Divider
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { userApi, featureFlagsApi } from '../services/api';
import Header from '../components/Header';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [error, setError] = useState('');
    const [showTruckEnter, setShowTruckEnter] = useState(true);
    const [showVanEnter, setShowVanEnter] = useState(true);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role: 'MANAGER'
    });
    const { t } = useTranslation();
    const { user: currentUser } = useAuth();
    const defaultAdminUsername = window._env_?.DEFAULT_ADMIN_USERNAME || process.env.DEFAULT_ADMIN_USERNAME || 'admin@sapphire.ro';
    
    // Debug logging
    console.log('Environment variables:', {
        windowEnv: window._env_?.DEFAULT_ADMIN_USERNAME,
        processEnv: process.env.DEFAULT_ADMIN_USERNAME,
        defaultAdminUsername
    });

    useEffect(() => {
        loadUsers();
        loadFeatureFlags();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await userApi.getUsers();
            // Filter out the current user from the list
            const filteredUsers = response.data.filter(user => user.email !== currentUser.email);
            setUsers(filteredUsers);
        } catch (error) {
            console.error('Failed to load users:', error);
            setError('Failed to load users');
        }
    };

    const loadFeatureFlags = async () => {
        try {
            const response = await featureFlagsApi.getFeatureFlags();
            const flags = response.data;
            setShowTruckEnter(flags.find(f => f.featureName === 'SHOW_TRUCK_ENTER_ENTRIES')?.featureEnabled ?? true);
            setShowVanEnter(flags.find(f => f.featureName === 'SHOW_VAN_ENTER_ENTRIES')?.featureEnabled ?? true);
        } catch (error) {
            console.error('Failed to load feature flags:', error);
            setError('Failed to load feature flags');
        }
    };

    const handleFeatureFlagChange = async (featureName, checked) => {
        try {
            await featureFlagsApi.updateFeatureFlag(featureName, checked);
            if (featureName === 'SHOW_TRUCK_ENTER_ENTRIES') {
                setShowTruckEnter(checked);
            } else if (featureName === 'SHOW_VAN_ENTER_ENTRIES') {
                setShowVanEnter(checked);
            }
        } catch (error) {
            console.error('Failed to update feature flag:', error);
            setError('Failed to update feature flag');
            // Revert the checkbox state
            if (featureName === 'SHOW_TRUCK_ENTER_ENTRIES') {
                setShowTruckEnter(!checked);
            } else if (featureName === 'SHOW_VAN_ENTER_ENTRIES') {
                setShowVanEnter(!checked);
            }
        }
    };

    const handleOpenDialog = (user = null) => {
        if (user) {
            setFormData({
                username: user.username,
                email: user.email,
                password: '',
                role: user.role
            });
            setSelectedUser(user);
        } else {
            setFormData({
                username: '',
                email: '',
                password: '',
                role: 'MANAGER'
            });
            setSelectedUser(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setFormData({
            username: '',
            email: '',
            password: '',
            role: 'MANAGER'
        });
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async () => {
        try {
            if (selectedUser) {
                await userApi.updateUser(selectedUser.id, formData);
            } else {
                await userApi.createUser(formData);
            }
            await loadUsers();
            handleCloseDialog();
        } catch (error) {
            setError(error.response?.data?.error || 'Operation failed');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm(t('common.confirmDelete'))) {
            try {
                await userApi.deleteUser(userId);
                await loadUsers();
            } catch (error) {
                setError('Failed to delete user');
            }
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flex: 1 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" component="h2">
                                    {t('admin.userManagement')}
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenDialog()}
                                >
                                    {t('admin.addUser')}
                                </Button>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('admin.username')}</TableCell>
                                            <TableCell>{t('admin.email')}</TableCell>
                                            <TableCell>{t('admin.role')}</TableCell>
                                            <TableCell align="right">{t('admin.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user) => {
                                            const isDefaultAdmin = user.email === defaultAdminUsername;
                                            return (
                                                <TableRow key={user.id}>
                                                    <TableCell>{user.username}</TableCell>
                                                    <TableCell>{user.email}</TableCell>
                                                    <TableCell>{t(`admin.roles.${user.role}`)}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={isDefaultAdmin ? t('admin.defaultAdminNoEdit') : ''}>
                                                            <span>
                                                                <IconButton
                                                                    color="primary"
                                                                    onClick={() => handleOpenDialog(user)}
                                                                    disabled={isDefaultAdmin}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title={isDefaultAdmin ? t('admin.defaultAdminNoDelete') : ''}>
                                                            <span>
                                                                <IconButton
                                                                    color="error"
                                                                    onClick={() => handleDeleteUser(user.id)}
                                                                    disabled={isDefaultAdmin}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
                                {t('admin.featureFlagManagement')}
                            </Typography>
                            <FormGroup>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <FormControlLabel
                                        sx={{
                                            margin: 0,
                                            '.MuiFormControlLabel-label': {
                                                flex: 1,
                                                wordBreak: 'break-word'
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                checked={showTruckEnter}
                                                onChange={(e) => handleFeatureFlagChange('SHOW_TRUCK_ENTER_ENTRIES', e.target.checked)}
                                            />
                                        }
                                        label={t('admin.showTruckEnter')}
                                    />
                                    <FormControlLabel
                                        sx={{
                                            margin: 0,
                                            '.MuiFormControlLabel-label': {
                                                flex: 1,
                                                wordBreak: 'break-word'
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                checked={showVanEnter}
                                                onChange={(e) => handleFeatureFlagChange('SHOW_VAN_ENTER_ENTRIES', e.target.checked)}
                                            />
                                        }
                                        label={t('admin.showVanEnter')}
                                    />
                                </Box>
                            </FormGroup>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>
                    {selectedUser ? t('admin.editUser') : t('admin.addUser')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="username"
                        label={t('admin.username')}
                        type="text"
                        fullWidth
                        value={formData.username}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        name="email"
                        label={t('admin.email')}
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        name="password"
                        label={t('admin.password')}
                        type="password"
                        fullWidth
                        value={formData.password}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        name="role"
                        label={t('admin.role')}
                        select
                        fullWidth
                        value={formData.role}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="ADMIN">{t('admin.roles.ADMIN')}</MenuItem>
                        <MenuItem value="MANAGER">{t('admin.roles.MANAGER')}</MenuItem>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {selectedUser ? t('common.save') : t('common.add')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminDashboard; 